import { createSlice, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { showApiErrors } from '@/utils';
import { DatePresetKey } from '@/models/cancelledOrders.model';
import { IAdminMainMetricsQuery, MainMetricsGroupBy } from '@/models/dashboard.model';
import { DashboardState, DASHBOARD_SLICE_NAME, initialState } from './models';
import {
  getDashboardData,
  getCityStatisticData,
  getBuyersData,
  getCategoryStatisticData,
  getTrends,
  getAdminMainMetricsData,
  getTopSuppliers,
  getTopProducts,
} from './actionCreators';
import { TDashboardCityStatisticQuery, TDashboardCategoriesStatisticQuery, normalizeDashboardTrends } from '../helpers';

export const slice = createSlice({
  name: DASHBOARD_SLICE_NAME,
  initialState,
  reducers: {
    changeCityQuery: (state: DashboardState, action: PayloadAction<TDashboardCityStatisticQuery>) => {
      state.cityStatisticQuery = action.payload;
    },
    changeBuyersQuery: (state: DashboardState, action: PayloadAction<TDashboardCityStatisticQuery>) => {
      state.buyersQuery = action.payload;
    },
    changeCategoriesQuery: (state: DashboardState, action: PayloadAction<TDashboardCategoriesStatisticQuery>) => {
      state.categoriesStatisticQuery = action.payload;
    },
    setMainMetricsQuery: (state: DashboardState, action: PayloadAction<IAdminMainMetricsQuery>) => {
      state.mainMetricsQuery = action.payload;
    },
    setMainMetricsPreset: (state: DashboardState, action: PayloadAction<DatePresetKey>) => {
      state.mainMetricsPreset = action.payload;
    },
    setMainMetricsGroupBy: (state: DashboardState, action: PayloadAction<MainMetricsGroupBy>) => {
      state.mainMetricsGroupBy = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardData.fulfilled, (state: DashboardState, action) => {
        state.dashboardData = action.payload.data;
        state.isLoading = false;
      })
      .addCase(getCityStatisticData.fulfilled, (state: DashboardState, action) => {
        state.cityStatisticData = action.payload.data;
        state.isLoading = false;
      })
      .addCase(getBuyersData.fulfilled, (state: DashboardState, action) => {
        state.buyersData = action.payload.data;
        state.isLoading = false;
      })
      .addCase(getCategoryStatisticData.fulfilled, (state: DashboardState, action) => {
        state.categoriesStatisticData = action.payload.data;
        state.isLoading = false;
      })
      .addCase(getTrends.fulfilled, (state: DashboardState, action) => {
        if (!state.dashboardData) return;
        state.dashboardData = {
          ...state.dashboardData,
          trends: normalizeDashboardTrends(state.dashboardData.trends, action.payload.data),
        };
        state.isLoading = false;
      })
      .addCase(getAdminMainMetricsData.fulfilled, (state, action) => {
        state.mainMetricsData = action.payload.data;
        state.isMainMetricsLoading = false;
      })
      .addCase(getTopSuppliers.fulfilled, (state, action) => {
        if (state.mainMetricsData) {
          state.mainMetricsData.top_suppliers = action.payload.data;
        }
      })
      .addCase(getTopProducts.fulfilled, (state, action) => {
        if (state.mainMetricsData) {
          state.mainMetricsData.top_products = action.payload.data;
        }
      })
      .addMatcher(
        isPending(getDashboardData, getCityStatisticData, getBuyersData, getCategoryStatisticData, getTrends),
        (state: DashboardState) => {
          state.isLoading = true;
          state.error = null;
        },
      )
      .addMatcher(isPending(getAdminMainMetricsData), (state) => {
        state.isMainMetricsLoading = true;
        state.error = null;
      })
      .addMatcher(
        isRejected(getDashboardData, getCityStatisticData, getBuyersData, getCategoryStatisticData, getTrends),
        (state: DashboardState, action) => {
          const { error } = action;
          state.isLoading = false;
          state.error = error;

          showApiErrors(error);
        },
      )
      .addMatcher(isRejected(getAdminMainMetricsData), (state, action) => {
        state.isMainMetricsLoading = false;
        state.error = action.error;
      });
  },
});

export default slice.reducer;

export const {
  changeCityQuery,
  changeBuyersQuery,
  changeCategoriesQuery,
  setMainMetricsQuery,
  setMainMetricsPreset,
  setMainMetricsGroupBy,
} = slice.actions;
